const CREATE_PATCH_KEYS = new Set([
  'species',
  'background',
  'charClass',
  'classLevelText',
  'level',
  'xp',
  'maxHp',
  'currentHp',
  'tempHp',
  'armorClass',
  'initiative',
  'speed',
  'baseStats',
  'skillProficiencies',
  'skillExpertise',
  'skillMiscMods',
  'savingThrowProficiencies',
  'abilities',
  'feats',
  'spells',
  'spellSlots',
  'spellcasting',
  'inventory',
  'wallet',
  'importCarrying',
  'importReceipt',
]);

const UPDATE_PATCH_KEYS = new Set([
  'baseStats',
  'charClass',
  'level',
  'maxHp',
  'currentHp',
  'tempHp',
  'armorClass',
  'initiative',
  'speed',
  'skillProficiencies',
  'skillExpertise',
  'skillMiscMods',
  'savingThrowProficiencies',
  'abilities',
  'feats',
  'spells',
  'spellSlots',
  'spellcasting',
  'inventory',
  'wallet',
  'importCarrying',
  'importReceipt',
]);

const CREATE_PAYLOAD_KEYS = new Set(['mode', 'createArgs', 'patch', 'importReceipt', 'warnings']);
const UPDATE_PAYLOAD_KEYS = new Set(['mode', 'patch', 'importReceipt', 'warnings']);
const CREATE_ARG_KEYS = new Set(['name', 'race', 'maxHp']);
const DEFAULT_IMPORT_API_PATH = '/importer/api/run';
const DEFAULT_IMPORT_UPLOAD_API_PATH = '/importer/api/upload';
const LOCAL_PREVIEW_DISABLED_MESSAGE = 'Character import server preview is local-only. Start the local import preview server on localhost, or paste sanitized app-payload JSON.';
export const IMPORT_PREVIEW_SOURCES = Object.freeze([
  { id: 'shenyi', label: 'DDB/Shenyi', sourceKind: 'ddb-pdf-fields' },
  { id: 'vental', label: 'DiceCloud/Vental', sourceKind: 'dicecloud-core-facts' },
]);
const IMPORT_PREVIEW_SOURCE_IDS = new Set(IMPORT_PREVIEW_SOURCES.map((source) => source.id));
const IMPORT_PREVIEW_SOURCE_KIND_BY_ID = new Map(IMPORT_PREVIEW_SOURCES.map((source) => [source.id, source.sourceKind]));
const APP_PAYLOAD_SOURCE_KINDS = new Set(IMPORT_PREVIEW_SOURCES.map((source) => source.sourceKind));

// User-facing labels for detected payload source kinds (Track B: the user
// uploads a PDF and the server's classifier decides — persona names like
// "Shenyi"/"Vental" stay confined to the dev fixture transport above).
const APP_PAYLOAD_SOURCE_KIND_LABELS = Object.freeze({
  'ddb-pdf-fields': 'DDB character sheet',
  'dicecloud-core-facts': 'DiceCloud character sheet',
});

export function describeAppPayloadSourceKind(sourceKind) {
  return APP_PAYLOAD_SOURCE_KIND_LABELS[sourceKind] || 'character sheet (unrecognized source)';
}
const DICECLOUD_SKILL_SAVE_PATCH_KEYS = [
  'skillProficiencies',
  'skillExpertise',
  'skillMiscMods',
  'savingThrowProficiencies',
];

const UNSAFE_NESTED_KEYS = new Set([
  'ddbImport',
  'body',
  'campaignId',
  'charId',
  'characterId',
  'contentHtml',
  'description',
  'descriptions',
  'details',
  'effectText',
  'flavorText',
  'blocks',
  'lines',
  'ocr',
  'providerDiagnostics',
  'providerResponse',
  'providerStderr',
  'providerStdout',
  'pages',
  'raw',
  'rawFields',
  'rawJson',
  'rawOcr',
  'rawOcrProviderResponse',
  'rawOcrText',
  'rawProviderText',
  'rawRegions',
  'rawSections',
  'rawSource',
  'rawText',
  'regionSnippets',
  'sourceFields',
  'sourceJson',
  'sourceText',
  'sourceUrl',
  'sourceUrls',
  'stderr',
  'stdout',
  'ocrReview',
  'reviewText',
  'rulesText',
  'message',
  'messages',
  'ownerId',
  'password',
  'privateText',
  'text',
  'textPreview',
  'userId',
  'words',
  'fullText',
  'html',
  'url',
  'urls',
]);
const UNSAFE_NESTED_KEY_LOOKUP = new Set(
  Array.from(UNSAFE_NESTED_KEYS).flatMap((key) => [key, key.toLowerCase()])
);
const NOTE_KEYS = new Set(['note', 'notes']);
const WARNING_KEYS = new Set(['equipmentDeferred', 'featureListReplacement', 'spellListReplacement']);
const SPELL_ROW_KEYS = new Set([
  'active',
  'attackType',
  'bonusDice',
  'bonusType',
  'casterOnly',
  'castingTime',
  'components',
  'concentration',
  'damageType',
  'dice',
  'duration',
  'effectRounds',
  'effectType',
  'healingDice',
  'id',
  'importWarnings',
  'level',
  'matchedBy',
  'name',
  'prepared',
  'range',
  'ritual',
  'saveAbility',
  'saveEffect',
  'school',
  'sourceKind',
  'spellType',
  'srdId',
  'targetType',
]);
const INVENTORY_ROW_KEYS = new Set([
  'abilities',
  'acBonus',
  'armorClass',
  'buffs',
  'category',
  'combatBonuses',
  'cost',
  'damage',
  'debuffs',
  'equipped',
  'favorited',
  'id',
  'importMetadata',
  'importWarnings',
  'itemId',
  'mastery',
  'matchedBy',
  'name',
  'notes',
  'onUseEffects',
  'price',
  'properties',
  'quantity',
  'range',
  'sourceKind',
  'srdCategory',
  'srdId',
  'srdKind',
  'weaponRange',
  'weight',
  'weightText',
]);
const RULE_ROW_KEYS = new Set([
  'canonicalName',
  'featureKind',
  'id',
  'importWarnings',
  'level',
  'matchedBy',
  'matchedSlug',
  'name',
  'owner',
  'sourceIndex',
  'sourceKind',
  'sourceSection',
  'srdId',
  'type',
]);
const IMPORT_METADATA_KEYS = new Set([
  'importWarnings',
  'importedFieldsPolicy',
  'matchedBy',
  'matchedSlug',
  'sourceKind',
  'srdId',
]);
const COST_KEYS = new Set(['denom', 'value']);
const DAMAGE_KEYS = new Set(['dice', 'type']);
const RANGE_KEYS = new Set(['long', 'normal']);
const RECEIPT_KEYS = new Set(['contentProvenance', 'diagnostics', 'policyVersion', 'sourceReceipt', 'structuredContent']);
const SOURCE_RECEIPT_KEYS = new Set(['meta', 'sourceKind', 'sourceVersion']);
const RECEIPT_META_KEYS = new Set([
  'equipmentEntryCount',
  'failedInflateCount',
  'featureEntryCount',
  'fieldCount',
  'flateStreamCount',
  'generatedFrom',
  'inflatedStreamCount',
  'layoutRegionCount',
  'manifestCount',
  'nonEmptyFieldCount',
  'objectCount',
  'orderedChunkCount',
  'orderedTextLength',
  'pageCount',
  'parser',
  'pdfVersion',
  'sectionCount',
  'spellEntryCount',
  'streamCount',
  'textChunkCount',
  'textHash',
  'textLength',
  'textStreamCount',
  'trustBoundary',
  'uniqueFieldCount',
  'weaponEntryCount',
]);
const DIAGNOSTIC_KEYS = new Set(['code', 'message', 'path', 'severity', 'value']);
const STRUCTURED_CONTENT_KEYS = new Set(['backgroundGrants', 'core', 'features', 'feats']);
const BACKGROUND_GRANT_KEYS = new Set([
  'backgroundName',
  'backgroundSrdId',
  'canonicalName',
  'grantPolicy',
  'grants',
  'sourceKind',
  'type',
]);
const CONTENT_PROVENANCE_KEYS = new Set([
  'canonicalName',
  'importWarnings',
  'matchedBy',
  'matchedSlug',
  'name',
  'sourceKind',
  'sourceRowIndex',
  'srdId',
  'target',
  'type',
]);
const TARGET_KEYS = new Set(['collection', 'rowId', 'rowIndex']);
const CARRYING_KEYS = new Set([
  'capacity',
  'current',
  'encumberedLbs',
  'encumberedText',
  'provenance',
  'pushDragLiftLbs',
  'pushDragLiftText',
  'weightCarriedLbs',
  'weightCarriedText',
]);
const PROVENANCE_KEYS = new Set(['sourceKind']);
const SPELLCASTING_KEYS = new Set(['ability', 'attackBonus', 'className', 'saveDC']);
const WALLET_KEYS = new Set(['copper', 'electrum', 'gold', 'platinum', 'silver']);
const BASE_STAT_KEYS = new Set(['cha', 'con', 'dex', 'int', 'str', 'wis']);
const CONTENT_ROW_SOURCE_KINDS = new Set(['srd', 'custom-placeholder']);
const CARRYING_PROVENANCE_SOURCE_KINDS = new Set(['character-fact']);
const INVENTORY_RUNTIME_HOOK_KEYS = ['abilities', 'buffs', 'debuffs', 'onUseEffects', 'combatBonuses'];
const PATCH_TEXT_FIELDS = new Set(['background', 'charClass', 'classLevelText', 'species']);
const CREATE_ARG_TEXT_FIELDS = new Set(['name', 'race']);
const SPELL_TEXT_FIELDS = new Set([
  'attackType',
  'bonusDice',
  'bonusType',
  'castingTime',
  'components',
  'damageType',
  'dice',
  'duration',
  'effectType',
  'healingDice',
  'importWarnings',
  'matchedBy',
  'name',
  'range',
  'saveAbility',
  'saveEffect',
  'school',
  'sourceKind',
  'spellType',
  'targetType',
]);
const INVENTORY_TEXT_FIELDS = new Set([
  'category',
  'importWarnings',
  'mastery',
  'matchedBy',
  'name',
  'properties',
  'range',
  'sourceKind',
  'srdCategory',
  'srdKind',
  'weaponRange',
  'weightText',
]);
const RULE_TEXT_FIELDS = new Set([
  'canonicalName',
  'featureKind',
  'importWarnings',
  'matchedBy',
  'matchedSlug',
  'name',
  'sourceKind',
  'sourceSection',
  'type',
]);
const CONTENT_PROVENANCE_TEXT_FIELDS = new Set([
  'canonicalName',
  'importWarnings',
  'matchedBy',
  'matchedSlug',
  'name',
  'sourceKind',
  'type',
]);
const BACKGROUND_GRANT_TEXT_FIELDS = new Set([
  'backgroundName',
  'canonicalName',
  'grantPolicy',
  'sourceKind',
  'type',
]);
const SPELLCASTING_TEXT_FIELDS = new Set(['ability', 'className']);
const RECEIPT_NUMERIC_META_KEYS = new Set([
  'equipmentEntryCount',
  'failedInflateCount',
  'featureEntryCount',
  'fieldCount',
  'flateStreamCount',
  'inflatedStreamCount',
  'layoutRegionCount',
  'manifestCount',
  'nonEmptyFieldCount',
  'objectCount',
  'orderedChunkCount',
  'orderedTextLength',
  'pageCount',
  'sectionCount',
  'spellEntryCount',
  'streamCount',
  'textChunkCount',
  'textLength',
  'textStreamCount',
  'uniqueFieldCount',
  'weaponEntryCount',
]);
const RECEIPT_META_POLICY = {
  'ddb-pdf-fields': {
    parser: 'bounded-acroform-v1',
    trustBoundary: 'ddb-pdf-fields-reader',
    generatedFrom: undefined,
    pdfVersion: /^[0-9.]{1,20}$/,
  },
  'dicecloud-core-facts': {
    parser: 'bounded-dicecloud-core-facts-v1',
    trustBoundary: 'dicecloud-core-facts-reader',
    generatedFrom: 'dicecloud-layout-profile',
    pdfVersion: undefined,
  },
};

function assertObject(value, message) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(message);
  }
}

function cloneJson(value) {
  if (value === undefined || value === null) return value;
  return JSON.parse(JSON.stringify(value));
}

function stableReceiptJson(value) {
  if (Array.isArray(value)) return value.map((entry) => stableReceiptJson(entry));
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.keys(value)
      .sort()
      .map((key) => [key, stableReceiptJson(value[key])]));
  }
  return value;
}

function assertReceiptParity(payload) {
  if (payload?.importReceipt === undefined || payload?.patch?.importReceipt === undefined) return;
  const rootReceipt = JSON.stringify(stableReceiptJson(payload.importReceipt));
  const patchReceipt = JSON.stringify(stableReceiptJson(payload.patch.importReceipt));
  if (rootReceipt !== patchReceipt) {
    throw new Error('Import review payload has inconsistent root and patch import receipts.');
  }
}

function assertAllowedKeys(value, allowedKeys, messagePrefix) {
  const unsupported = Object.keys(value || {}).filter((key) => !allowedKeys.has(key)).sort();
  if (unsupported.length) {
    throw new Error(`${messagePrefix}: ${unsupported.join(', ')}`);
  }
}

function assertNoUnsafeNestedKeys(value, path = 'payload') {
  if (!value || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach((child, index) => assertNoUnsafeNestedKeys(child, `${path}[${index}]`));
    return;
  }
  for (const [key, child] of Object.entries(value)) {
    if (NOTE_KEYS.has(key.toLowerCase()) && hasNonEmptyText(child)) {
      throw new Error(`Import payload contains unsafe nested field: ${path}.${key}`);
    }
    if (UNSAFE_NESTED_KEY_LOOKUP.has(key) || UNSAFE_NESTED_KEY_LOOKUP.has(key.toLowerCase())) {
      throw new Error(`Import payload contains unsafe nested field: ${path}.${key}`);
    }
    assertNoUnsafeNestedKeys(child, `${path}.${key}`);
  }
}

function assertAllowedNestedKeys(value, allowedKeys, path) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return;
  const unsupported = Object.keys(value).filter((key) => !allowedKeys.has(key)).sort();
  if (unsupported.length) {
    throw new Error(`Import payload contains unsupported nested field(s) at ${path}: ${unsupported.join(', ')}`);
  }
}

function assertAllowedSourceKind(value, allowedKinds, path) {
  if (value === undefined) return;
  if (typeof value !== 'string' || !allowedKinds.has(value)) {
    throw new Error(`Import payload contains unsupported source kind at ${path}: ${value}`);
  }
}

function assertAllowedTextValue(value, path, maxLength = 200) {
  if (value === undefined || value === null) return;
  if (Array.isArray(value)) {
    value.forEach((entry, index) => assertAllowedTextValue(entry, `${path}[${index}]`, maxLength));
    return;
  }
  if (typeof value !== 'string') return;
  const text = value.trim();
  if (!text) return;
  if (/\.importWarnings(?:\[|$)/.test(path) && /^[a-z0-9][a-z0-9-]{0,119}$/.test(text)) return;
  const wordCount = (text.match(/[A-Za-z0-9]+/g) || []).length;
  if (
    text.length > maxLength
    || /[\r\n<>`{}]/.test(text)
    || /https?:\/\//i.test(text)
    || /\b(?:copyright|copied|description|full text|private|prose|raw text|sentence|source text)\b/i.test(text)
    || (/[.!?]/.test(text) && wordCount > 3)
    || wordCount > 16
  ) {
    throw new Error(`Import payload contains unsupported text value at ${path}.`);
  }
}

function assertAllowedTextFields(value, fields, path, maxLength = 200) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return;
  for (const key of fields) {
    if (Object.hasOwn(value, key)) {
      assertAllowedTextValue(value[key], `${path}.${key}`, maxLength);
    }
  }
}

function assertAllowedRows(values, allowedKeys, path, textFields = new Set()) {
  if (!Array.isArray(values)) return;
  values.forEach((entry, index) => {
    const rowPath = `${path}[${index}]`;
    if (typeof entry === 'string') {
      assertAllowedTextValue(entry, rowPath);
      return;
    }
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      throw new Error(`Import payload contains unsupported row at ${rowPath}.`);
    }
    assertAllowedNestedKeys(entry, allowedKeys, rowPath);
    assertAllowedTextFields(entry, textFields, rowPath);
  });
}

function assertAllowedMap(value, allowedKeys, path) {
  if (value === undefined) return;
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`Import payload field must be an object: ${path}`);
  }
  assertAllowedNestedKeys(value, allowedKeys, path);
}

function assertAllowedWarningValues(value, path) {
  if (value === undefined) return;
  assertAllowedMap(value, WARNING_KEYS, path);
  for (const [key, child] of Object.entries(value)) {
    if (typeof child !== 'boolean') {
      throw new Error(`Import payload warning must be boolean: ${path}.${key}`);
    }
  }
}

function assertAllowedImportMetadata(value, path) {
  if (value === undefined) return;
  assertAllowedMap(value, IMPORT_METADATA_KEYS, path);
  assertAllowedSourceKind(value.sourceKind, CONTENT_ROW_SOURCE_KINDS, `${path}.sourceKind`);
}

function assertAllowedContentRows(values, allowedKeys, path) {
  assertAllowedRows(values, allowedKeys, path, allowedKeys === CONTENT_PROVENANCE_KEYS
    ? CONTENT_PROVENANCE_TEXT_FIELDS
    : allowedKeys === BACKGROUND_GRANT_KEYS
      ? BACKGROUND_GRANT_TEXT_FIELDS
      : RULE_TEXT_FIELDS);
  (values || []).forEach((entry, index) => {
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      assertAllowedSourceKind(entry.sourceKind, CONTENT_ROW_SOURCE_KINDS, `${path}[${index}].sourceKind`);
    }
  });
}

function assertEmptyRuntimeHookRows(value, path) {
  if (value === undefined) return;
  if (!Array.isArray(value)) {
    throw new Error(`Import payload field must be an array: ${path}`);
  }
  if (value.length) {
    throw new Error(`Import payload contains unsupported item runtime hook(s) at ${path}.`);
  }
}

function assertAllowedDiagnostics(values, path) {
  assertAllowedRows(values, DIAGNOSTIC_KEYS, path);
  (values || []).forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;
    const entryPath = `${path}[${index}]`;
    if (entry.message !== undefined && hasNonEmptyText(entry.message)) {
      throw new Error(`Import payload contains unsupported diagnostic message at ${entryPath}.message.`);
    }
    if (entry.value !== undefined && entry.value !== null) {
      throw new Error(`Import payload contains unsupported diagnostic value at ${entryPath}.value.`);
    }
    if (entry.code !== undefined && !/^[a-z0-9-]{1,120}$/.test(String(entry.code))) {
      throw new Error(`Import payload contains unsupported diagnostic code at ${entryPath}.code.`);
    }
    if (entry.severity !== undefined && !/^(info|warning|error)$/.test(String(entry.severity))) {
      throw new Error(`Import payload contains unsupported diagnostic severity at ${entryPath}.severity.`);
    }
    if (entry.path !== undefined && !/^[A-Za-z0-9_.#[\]-]{1,180}$/.test(String(entry.path))) {
      throw new Error(`Import payload contains unsupported diagnostic path at ${entryPath}.path.`);
    }
  });
}

function assertAllowedReceiptMeta(sourceReceipt, path) {
  const meta = sourceReceipt?.meta;
  if (meta === undefined) return;
  assertAllowedMap(meta, RECEIPT_META_KEYS, `${path}.meta`);
  if (!APP_PAYLOAD_SOURCE_KINDS.has(sourceReceipt.sourceKind)) return;
  const policy = RECEIPT_META_POLICY[sourceReceipt.sourceKind] || {};
  for (const [key, value] of Object.entries(meta)) {
    const metaPath = `${path}.meta.${key}`;
    if (RECEIPT_NUMERIC_META_KEYS.has(key)) {
      if (!Number.isFinite(value) || value < 0 || value > 1000000) {
        throw new Error(`Import payload contains unsupported receipt metadata value at ${metaPath}.`);
      }
      continue;
    }
    if (key === 'parser' || key === 'trustBoundary' || key === 'generatedFrom') {
      if (value !== policy[key]) {
        throw new Error(`Import payload contains unsupported receipt metadata value at ${metaPath}.`);
      }
      continue;
    }
    if (key === 'pdfVersion') {
      if (!policy.pdfVersion || typeof value !== 'string' || !policy.pdfVersion.test(value)) {
        throw new Error(`Import payload contains unsupported receipt metadata value at ${metaPath}.`);
      }
      continue;
    }
    throw new Error(`Import payload contains unsupported receipt metadata value at ${metaPath}.`);
  }
}

function assertAllowedNestedReceipt(receipt, path) {
  if (receipt === undefined) return;
  assertAllowedMap(receipt, RECEIPT_KEYS, path);
  if (receipt.sourceReceipt !== undefined) {
    assertAllowedMap(receipt.sourceReceipt, SOURCE_RECEIPT_KEYS, `${path}.sourceReceipt`);
    assertAllowedReceiptMeta(receipt.sourceReceipt, `${path}.sourceReceipt`);
  }
  assertAllowedDiagnostics(receipt.diagnostics, `${path}.diagnostics`);
  if (receipt.structuredContent !== undefined) {
    assertAllowedMap(receipt.structuredContent, STRUCTURED_CONTENT_KEYS, `${path}.structuredContent`);
    assertAllowedContentRows(receipt.structuredContent.core, CONTENT_PROVENANCE_KEYS, `${path}.structuredContent.core`);
    assertAllowedContentRows(receipt.structuredContent.features, RULE_ROW_KEYS, `${path}.structuredContent.features`);
    assertAllowedContentRows(receipt.structuredContent.feats, RULE_ROW_KEYS, `${path}.structuredContent.feats`);
    assertAllowedContentRows(receipt.structuredContent.backgroundGrants, BACKGROUND_GRANT_KEYS, `${path}.structuredContent.backgroundGrants`);
  }
  assertAllowedContentRows(receipt.contentProvenance, CONTENT_PROVENANCE_KEYS, `${path}.contentProvenance`);
  (receipt.contentProvenance || []).forEach((entry, index) => {
    if (entry?.target !== undefined) assertAllowedMap(entry.target, TARGET_KEYS, `${path}.contentProvenance[${index}].target`);
  });
}

function assertAllowedPatchShape(patch) {
  assertAllowedTextFields(patch, PATCH_TEXT_FIELDS, 'patch');
  assertAllowedMap(patch.baseStats, BASE_STAT_KEYS, 'patch.baseStats');
  assertAllowedMap(patch.wallet, WALLET_KEYS, 'patch.wallet');
  assertAllowedMap(patch.importCarrying, CARRYING_KEYS, 'patch.importCarrying');
  if (patch.importCarrying?.provenance !== undefined) {
    assertAllowedMap(patch.importCarrying.provenance, PROVENANCE_KEYS, 'patch.importCarrying.provenance');
    assertAllowedSourceKind(
      patch.importCarrying.provenance.sourceKind,
      CARRYING_PROVENANCE_SOURCE_KINDS,
      'patch.importCarrying.provenance.sourceKind'
    );
  }
  assertAllowedMap(patch.spellcasting, SPELLCASTING_KEYS, 'patch.spellcasting');
  assertAllowedTextFields(patch.spellcasting, SPELLCASTING_TEXT_FIELDS, 'patch.spellcasting', 80);
  assertAllowedRows(patch.spells, SPELL_ROW_KEYS, 'patch.spells', SPELL_TEXT_FIELDS);
  assertAllowedRows(patch.inventory, INVENTORY_ROW_KEYS, 'patch.inventory', INVENTORY_TEXT_FIELDS);
  assertAllowedRows(patch.abilities, RULE_ROW_KEYS, 'patch.abilities', RULE_TEXT_FIELDS);
  assertAllowedRows(patch.feats, RULE_ROW_KEYS, 'patch.feats', RULE_TEXT_FIELDS);
  (patch.spells || []).forEach((spell, index) => {
    assertAllowedSourceKind(spell?.sourceKind, CONTENT_ROW_SOURCE_KINDS, `patch.spells[${index}].sourceKind`);
    assertAllowedImportMetadata(spell?.importMetadata, `patch.spells[${index}].importMetadata`);
  });
  (patch.inventory || []).forEach((item, index) => {
    assertAllowedSourceKind(item?.sourceKind, CONTENT_ROW_SOURCE_KINDS, `patch.inventory[${index}].sourceKind`);
    assertAllowedImportMetadata(item?.importMetadata, `patch.inventory[${index}].importMetadata`);
    if (item?.cost !== undefined) assertAllowedMap(item.cost, COST_KEYS, `patch.inventory[${index}].cost`);
    if (item?.damage !== undefined) assertAllowedMap(item.damage, DAMAGE_KEYS, `patch.inventory[${index}].damage`);
    if (item?.range !== undefined) assertAllowedMap(item.range, RANGE_KEYS, `patch.inventory[${index}].range`);
    INVENTORY_RUNTIME_HOOK_KEYS.forEach((key) => {
      assertEmptyRuntimeHookRows(item?.[key], `patch.inventory[${index}].${key}`);
    });
  });
  (patch.abilities || []).forEach((entry, index) => {
    assertAllowedSourceKind(entry?.sourceKind, CONTENT_ROW_SOURCE_KINDS, `patch.abilities[${index}].sourceKind`);
  });
  (patch.feats || []).forEach((entry, index) => {
    assertAllowedSourceKind(entry?.sourceKind, CONTENT_ROW_SOURCE_KINDS, `patch.feats[${index}].sourceKind`);
  });
  assertAllowedNestedReceipt(patch.importReceipt, 'patch.importReceipt');
}

function assertAllowedPayloadShape(payload) {
  assertAllowedTextFields(payload.createArgs, CREATE_ARG_TEXT_FIELDS, 'createArgs');
  assertAllowedPatchShape(payload.patch || {});
  assertAllowedNestedReceipt(payload.importReceipt, 'importReceipt');
  assertAllowedWarningValues(payload.warnings, 'warnings');
}

function hasNonEmptyText(value) {
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some((entry) => hasNonEmptyText(entry));
  if (value && typeof value === 'object') return Object.values(value).some((entry) => hasNonEmptyText(entry));
  return false;
}

function listNames(values, limit = 8) {
  if (!Array.isArray(values)) return [];
  return values
    .map((entry) => (typeof entry === 'string' ? entry : entry?.name))
    .filter((name) => typeof name === 'string' && name.trim())
    .slice(0, limit);
}

function getReceipt(payload) {
  return payload?.patch?.importReceipt || payload?.importReceipt || {};
}

function sourceKindsForPayload(payload) {
  return [
    payload?.importReceipt?.sourceReceipt?.sourceKind,
    payload?.patch?.importReceipt?.sourceReceipt?.sourceKind,
  ].filter((sourceKind) => typeof sourceKind === 'string' && sourceKind.trim());
}

function sourceKindForPayload(payload) {
  return sourceKindsForPayload(payload)[0] || 'unknown';
}

function contentRows(payload) {
  const rows = getReceipt(payload).contentProvenance;
  return Array.isArray(rows) ? rows : [];
}

function diagnosticRows(payload) {
  const rows = getReceipt(payload).diagnostics;
  return Array.isArray(rows) ? rows : [];
}

function normalizeImportPreviewSource(source) {
  const normalized = String(source || 'shenyi').trim().toLowerCase();
  if (!IMPORT_PREVIEW_SOURCE_IDS.has(normalized)) {
    throw new Error(`Unsupported import preview source: ${source || 'unknown'}.`);
  }
  return normalized;
}

function assertSupportedAppPayloadSourceKind(payload) {
  const sourceKinds = sourceKindsForPayload(payload);
  if (!sourceKinds.length) {
    throw new Error('Import review payload source is not enabled for app review: unknown.');
  }
  const unsupported = sourceKinds.find((sourceKind) => !APP_PAYLOAD_SOURCE_KINDS.has(sourceKind));
  if (unsupported) {
    throw new Error(`Import review payload source is not enabled for app review: ${unsupported}.`);
  }
  if (new Set(sourceKinds).size > 1) {
    throw new Error(`Import review payload has inconsistent app review sources: ${sourceKinds.join(', ')}.`);
  }
}

function assertExpectedAppPayloadSourceKind(payload, expectedSourceId) {
  const expectedSourceKind = IMPORT_PREVIEW_SOURCE_KIND_BY_ID.get(expectedSourceId);
  if (!expectedSourceKind) return;
  const actualSourceKind = sourceKindForPayload(payload);
  if (actualSourceKind !== expectedSourceKind) {
    throw new Error(`Import review payload source mismatch: expected ${expectedSourceKind}, received ${actualSourceKind}.`);
  }
}

function assertSourceSpecificPayloadPolicy(payload) {
  if (sourceKindForPayload(payload) !== 'dicecloud-core-facts') return;

  const patch = payload.patch || {};
  if (payload.mode === 'update') {
    const skillSaveKeys = DICECLOUD_SKILL_SAVE_PATCH_KEYS.filter((key) => Object.hasOwn(patch, key));
    if (skillSaveKeys.length) {
      throw new Error(`DiceCloud import payload cannot patch omitted skill/save field(s): ${skillSaveKeys.join(', ')}`);
    }
  }
  if (payload.mode === 'update' && Array.isArray(patch.spells) && payload.warnings?.spellListReplacement !== true) {
    throw new Error('DiceCloud update payloads that include spells must declare spellListReplacement.');
  }
  if (
    payload.mode === 'update'
    && (Array.isArray(patch.abilities) || Array.isArray(patch.feats))
    && payload.warnings?.featureListReplacement !== true
  ) {
    throw new Error('DiceCloud update payloads that include abilities or feats must declare featureListReplacement.');
  }
}

function importApiUrl(apiBase, path) {
  if (!apiBase) return path;
  const cleanBase = String(apiBase).replace(/\/$/, '');
  const pathOnly = path.includes('?') ? path.slice(0, path.indexOf('?')) : path;
  const query = path.includes('?') ? path.slice(path.indexOf('?')) : '';
  const endpointMatch = pathOnly.match(/\/api\/([^/?#]+)$/i);
  const endpoint = endpointMatch?.[1] || 'run';
  if (/\/api\/(?:run|upload)$/i.test(cleanBase)) {
    return `${cleanBase.replace(/\/api\/(?:run|upload)$/i, `/api/${endpoint}`)}${query}`;
  }
  if (/\/(?:importer|import)$/i.test(cleanBase)) return `${cleanBase}/api/${endpoint}${query}`;
  return `${cleanBase}${path}`;
}

function importPreviewFailureMessage(json, transport) {
  if (transport?.scope === 'remote-preview-opt-in') {
    return 'Import preview failed. Check the trusted import API logs or paste sanitized app-payload JSON.';
  }
  const message = typeof json?.error?.message === 'string'
    ? json.error.message.trim()
    : '';
  if (!message) return 'Import preview failed.';
  try {
    assertAllowedTextValue(message, 'preview.error.message', 160);
  } catch {
    return 'Import preview failed.';
  }
  return message;
}

function currentLocation() {
  if (typeof window !== 'undefined' && window.location) return window.location;
  return null;
}

function hostnameFromLocation(locationLike) {
  if (!locationLike) return '';
  if (typeof locationLike.hostname === 'string') return locationLike.hostname;
  if (typeof locationLike.href === 'string') {
    try {
      return new URL(locationLike.href).hostname;
    } catch {
      return '';
    }
  }
  return '';
}

function isLoopbackHostname(hostname) {
  const host = String(hostname || '').toLowerCase().replace(/^\[|\]$/g, '');
  return host === 'localhost'
    || host === '::1'
    || host === '0:0:0:0:0:0:0:1'
    || /^127(?:\.\d{1,3}){3}$/.test(host);
}

function isRemoteApiAllowed(allowRemoteApi) {
  return allowRemoteApi === true
    || allowRemoteApi === '1'
    || process.env.REACT_APP_IMPORT_ALLOW_REMOTE_API === '1';
}

export function getImportPreviewTransport({
  apiBase = '',
  locationLike = currentLocation(),
  allowRemoteApi = false,
} = {}) {
  const cleanBase = String(apiBase || '').trim().replace(/\/$/, '');
  const remoteAllowed = isRemoteApiAllowed(allowRemoteApi);
  if (cleanBase) {
    let url;
    try {
      url = new URL(cleanBase, locationLike?.href || 'http://localhost/');
    } catch {
      return {
        available: false,
        apiBase: cleanBase,
        apiPath: DEFAULT_IMPORT_API_PATH,
        reason: 'invalid-api-base',
        message: 'Import preview API base is invalid. Paste sanitized app-payload JSON instead.',
      };
    }
    const local = isLoopbackHostname(url.hostname);
    if (local || remoteAllowed) {
      return {
        available: true,
        apiBase: cleanBase,
        apiPath: DEFAULT_IMPORT_API_PATH,
        reason: null,
        scope: local ? 'local-preview-server' : 'remote-preview-opt-in',
      };
    }
    return {
      available: false,
      apiBase: cleanBase,
      apiPath: DEFAULT_IMPORT_API_PATH,
      reason: 'remote-api-disabled',
      message: 'Remote import preview APIs are disabled. Paste sanitized app-payload JSON or explicitly enable a trusted import API.',
    };
  }

  const hostname = hostnameFromLocation(locationLike);
  if (isLoopbackHostname(hostname)) {
    return {
      available: true,
      apiBase: '',
      apiPath: DEFAULT_IMPORT_API_PATH,
      reason: null,
      scope: 'same-origin-local-preview',
    };
  }
  return {
    available: false,
    apiBase: '',
    apiPath: DEFAULT_IMPORT_API_PATH,
    reason: 'local-only',
    message: LOCAL_PREVIEW_DISABLED_MESSAGE,
  };
}

export function validateImportReviewPayload(payload, options = {}) {
  assertObject(payload, 'Import review payload is required.');
  if (payload.mode !== 'create' && payload.mode !== 'update') {
    throw new Error('Import review payload mode must be create or update.');
  }

  const allowedPayloadKeys = payload.mode === 'create' ? CREATE_PAYLOAD_KEYS : UPDATE_PAYLOAD_KEYS;
  const allowedPatchKeys = payload.mode === 'create' ? CREATE_PATCH_KEYS : UPDATE_PATCH_KEYS;
  assertAllowedKeys(payload, allowedPayloadKeys, 'Import review payload has unsupported field(s)');
  assertObject(payload.patch, 'Import review payload requires a patch.');
  assertAllowedKeys(payload.patch, allowedPatchKeys, 'Import review patch has unsupported field(s)');
  assertNoUnsafeNestedKeys(payload.patch, 'patch');

  if (payload.mode === 'create') {
    assertObject(payload.createArgs, 'Create import payload requires create arguments.');
    assertAllowedKeys(payload.createArgs, CREATE_ARG_KEYS, 'Create import payload has unsupported create argument(s)');
    assertNoUnsafeNestedKeys(payload.createArgs, 'createArgs');
  }
  if (payload.importReceipt !== undefined) assertNoUnsafeNestedKeys(payload.importReceipt, 'importReceipt');
  if (payload.warnings !== undefined) assertNoUnsafeNestedKeys(payload.warnings, 'warnings');
  assertAllowedPayloadShape(payload);

  if (payload.mode === 'update' && Object.keys(payload.patch).filter((key) => key !== 'importReceipt').length === 0) {
    throw new Error('Update import payload has no app fields to review.');
  }

  assertSupportedAppPayloadSourceKind(payload);
  assertReceiptParity(payload);
  if (options.expectedSource) assertExpectedAppPayloadSourceKind(payload, options.expectedSource);
  assertSourceSpecificPayloadPolicy(payload);

  return cloneJson(payload);
}

export function summarizeImportReviewPayload(payload) {
  const safePayload = validateImportReviewPayload(payload);
  const patch = safePayload.patch || {};
  const provenance = contentRows(safePayload);
  const diagnostics = diagnosticRows(safePayload);
  const srdRows = provenance.filter((entry) => entry?.sourceKind === 'srd');
  const placeholderRows = provenance.filter((entry) => entry?.sourceKind === 'custom-placeholder');
  const sourceKind = getReceipt(safePayload).sourceReceipt?.sourceKind || 'unknown';
  const equipmentIncluded = Array.isArray(patch.inventory) && patch.inventory.length > 0;
  const equipmentDeferred = safePayload.warnings?.equipmentDeferred === true;

  return {
    mode: safePayload.mode,
    sourceKind,
    characterName: safePayload.createArgs?.name || patch.name || null,
    race: safePayload.createArgs?.race || null,
    maxHp: safePayload.createArgs?.maxHp ?? patch.maxHp ?? null,
    level: patch.level ?? null,
    charClass: patch.charClass || null,
    patchKeys: Object.keys(patch).filter((key) => key !== 'importReceipt').sort(),
    counts: {
      abilities: Array.isArray(patch.abilities) ? patch.abilities.length : 0,
      feats: Array.isArray(patch.feats) ? patch.feats.length : 0,
      spells: Array.isArray(patch.spells) ? patch.spells.length : 0,
      inventory: Array.isArray(patch.inventory) ? patch.inventory.length : 0,
      srd: srdRows.length,
      placeholders: placeholderRows.length,
      diagnostics: diagnostics.length,
    },
    equipmentStatus: equipmentIncluded ? 'included' : equipmentDeferred ? 'deferred' : 'not-present',
    warnings: {
      equipmentDeferred,
      featureListReplacement: safePayload.warnings?.featureListReplacement === true,
      spellListReplacement: safePayload.warnings?.spellListReplacement === true,
      sourceSrdLevelConflict: diagnostics.some((entry) => entry?.code === 'source-srd-level-conflict'),
      skillsOmitted: !Object.hasOwn(patch, 'skillProficiencies')
        && !Object.hasOwn(patch, 'skillExpertise')
        && !Object.hasOwn(patch, 'skillMiscMods'),
      savesOmitted: !Object.hasOwn(patch, 'savingThrowProficiencies'),
      attackRowsReviewOnly: Number.isFinite(getReceipt(safePayload).sourceReceipt?.meta?.weaponEntryCount)
        && getReceipt(safePayload).sourceReceipt.meta.weaponEntryCount > 0,
      preparedStateUnknown: Array.isArray(patch.spells)
        && patch.spells.length > 0
        && !patch.spells.some((spell) => Object.hasOwn(spell || {}, 'prepared')),
    },
    samples: {
      srd: listNames(srdRows),
      placeholders: listNames(placeholderRows),
      spells: listNames(patch.spells),
      inventory: listNames(patch.inventory),
      diagnostics: diagnostics
        .map((entry) => [entry?.severity, entry?.code].filter(Boolean).join(': '))
        .filter(Boolean)
        .slice(0, 8),
    },
    // Track D (docs/IMPORT-INTENT-PROBE.md F5+F6): the receipt's full
    // provenance and diagnostics, shaped for display. The samples above stay
    // as the at-a-glance layer (P2 depth-on-demand); these are the complete
    // receipt so the review can show exactly what matched the SRD, what
    // imported as a name-only placeholder, and every diagnostic.
    coverage: buildCoverageSummary(provenance),
    provenance: provenance.map((entry) => ({
      name: [entry?.name, entry?.canonicalName]
        .find((value) => typeof value === 'string' && value.trim()) || 'Unnamed entry',
      sourceKind: typeof entry?.sourceKind === 'string' ? entry.sourceKind : 'unknown',
      collection: provenanceCollection(entry),
      srdId: typeof entry?.srdId === 'string' ? entry.srdId : null,
    })),
    diagnosticsList: diagnostics.map((entry) => ({
      severity: typeof entry?.severity === 'string' ? entry.severity : 'info',
      code: typeof entry?.code === 'string' ? entry.code : 'unknown',
      path: typeof entry?.path === 'string' ? entry.path : null,
    })),
  };
}

function provenanceCollection(entry) {
  if (typeof entry?.target?.collection === 'string' && entry.target.collection) {
    return entry.target.collection;
  }
  if (typeof entry?.type === 'string' && entry.type) return entry.type;
  return 'other';
}

function buildCoverageSummary(provenance) {
  const byCollection = {};
  let srd = 0;
  let placeholders = 0;
  for (const entry of provenance) {
    const kind = entry?.sourceKind;
    if (kind !== 'srd' && kind !== 'custom-placeholder') continue;
    const collection = provenanceCollection(entry);
    if (!byCollection[collection]) byCollection[collection] = { srd: 0, placeholders: 0 };
    if (kind === 'srd') {
      srd += 1;
      byCollection[collection].srd += 1;
    } else {
      placeholders += 1;
      byCollection[collection].placeholders += 1;
    }
  }
  return {
    srd,
    placeholders,
    total: srd + placeholders,
    byCollection,
  };
}

export async function fetchImportAppPayload({
  source = 'shenyi',
  mode = 'update',
  includeEquipment = false,
  apiBase = '',
  locationLike = currentLocation(),
  allowRemoteApi = false,
  fetchImpl = fetch,
} = {}) {
  const normalizedSource = normalizeImportPreviewSource(source);
  const transport = getImportPreviewTransport({ apiBase, locationLike, allowRemoteApi });
  if (!transport.available) {
    throw new Error(transport.message || LOCAL_PREVIEW_DISABLED_MESSAGE);
  }
  const params = new URLSearchParams({
    source: normalizedSource,
    mode: 'app-payload',
    appMode: mode === 'create' ? 'create' : 'update',
    srd: '1',
  });
  if (includeEquipment) params.set('includeEquipment', '1');
  if (mode === 'create') params.set('reviewOnlyCreate', '1');

  const response = await fetchImpl(importApiUrl(transport.apiBase, `${transport.apiPath}?${params.toString()}`));
  const text = await response.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error('Import preview server did not return JSON. Start the local import preview server or paste sanitized app-payload JSON.');
  }
  if (!response.ok) {
    throw new Error(importPreviewFailureMessage(json, transport));
  }
  return validateImportReviewPayload(json, { expectedSource: normalizedSource });
}

export async function fetchImportPdfUploadAppPayload({
  source = 'shenyi',
  mode = 'create',
  includeEquipment = false,
  file,
  apiBase = '',
  locationLike = currentLocation(),
  allowRemoteApi = false,
  fetchImpl = fetch,
} = {}) {
  const normalizedSource = normalizeImportPreviewSource(source);
  if (normalizedSource !== 'shenyi') {
    throw new Error('PDF upload preview is only available for DDB/Shenyi.');
  }
  if (!file) {
    throw new Error('Choose a PDF file before uploading.');
  }
  const transport = getImportPreviewTransport({ apiBase, locationLike, allowRemoteApi });
  if (!transport.available) {
    throw new Error(transport.message || LOCAL_PREVIEW_DISABLED_MESSAGE);
  }
  const params = new URLSearchParams({
    source: normalizedSource,
    appMode: mode === 'create' ? 'create' : 'update',
    srd: '1',
  });
  if (includeEquipment) params.set('includeEquipment', '1');
  if (mode === 'create') params.set('reviewOnlyCreate', '1');
  if (typeof file.name === 'string' && file.name.trim()) params.set('fileName', file.name.trim());

  const response = await fetchImpl(importApiUrl(transport.apiBase, `${DEFAULT_IMPORT_UPLOAD_API_PATH}?${params.toString()}`), {
    method: 'POST',
    headers: {
      'content-type': file.type || 'application/pdf',
    },
    body: file,
  });
  const text = await response.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error('Import preview server did not return JSON. Start the local import preview server or paste sanitized app-payload JSON.');
  }
  if (!response.ok) {
    throw new Error(importPreviewFailureMessage(json, transport));
  }
  return validateImportReviewPayload(json, { expectedSource: normalizedSource });
}

export async function fetchDdbAppPayload(options = {}) {
  return fetchImportAppPayload({
    ...options,
    source: 'shenyi',
  });
}
